import React, { useEffect, useState } from 'react';
import { Button, Chip, Divider, Input, ModalBody, ModalFooter, ModalHeader, Select, SelectItem, Spinner, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow } from '@nextui-org/react';
import { IconSearch, IconUser, IconX } from '@tabler/icons-react';
import toast from 'react-hot-toast';
import moment from 'moment';
import { classNames, clone, Constants, formatAmount, sanitizeToNumbers } from '../../../utils';
import { GeneralService, PaymentsService, UsersService } from '../../../services';

const PM = Constants.PAYMENT.METHOD;
const FOREIGN_METHODS = [PM.ZELLE, PM.CASH_USD];

const round = (amount) => {
  return parseFloat(parseFloat(amount).toFixed(2));
}

const PAYMENT_METHODS = [
  { value: String(PM.TRANSFER), label: 'Débito Inmediato' },
  { value: String(PM.MOBILE), label: 'Pago móvil' },
  { value: String(PM.CASH_USD), label: 'Efectivo $' },
  { value: String(PM.ZELLE), label: 'Zelle' },
  { value: String(PM.TRANSFER_MANUAL), label: 'Transferencia' },
];

const PAYMENT_EXTRA_FIELDS = {
  [PM.TRANSFER]: ['bankId', 'dniType', 'dni', 'phoneCode', 'phone'],
  [PM.MOBILE]: ['bankId', 'dniType', 'dni', 'phoneCode', 'phone', 'date', 'reference'],
  [PM.CASH_USD]: ['bankId', 'dniType', 'dni', 'date', 'reference'],
  [PM.ZELLE]: ['name', 'account', 'reference', 'date'],
  [PM.TRANSFER_MANUAL]: ['bankId', 'dniType', 'dni', 'phoneCode', 'phone', 'date', 'reference'],
};

const FIELD_NAMES = {
  bankId: 'Banco',
  dniType: 'Tipo de documento',
  dni: 'Cédula',
  phoneCode: 'Código de área',
  phone: 'Teléfono',
  date: 'Fecha de pago',
  reference: 'Referencia',
  name: 'Nombre',
  account: 'Correo',
}

const getReadableStatus = (cutoffDate) => {
  const daysDiff = moment(cutoffDate).diff(moment(moment().format('YYYY-MM-DD')), 'days');
  if (daysDiff < 1) return 'Vencida';
  if (daysDiff === 1) return 'Vence mañana';
  if (daysDiff <= 5) return `Vence en ${daysDiff} días`;
  return 'Por vencer';
};

const getReadableStatusColor = (cutoffDate) => {
  const daysDiff = moment(cutoffDate).diff(moment(moment().format('YYYY-MM-DD')), 'days');
  if (daysDiff < 1) return 'text-red';
  if (daysDiff === 1) return 'text-red';
  if (daysDiff <= 5) return 'text-red';
  return 'text-blue';
};

const ModalCreatePayment = ({ onClose, onSuccess }) => {
  const [searchDni, setSearchDni] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  const [pendingPayment, setPendingPayment] = useState([]);
  const [isLoadingSubs, setIsLoadingSubs] = useState(false);
  const [fees, setFees] = useState([]);
  const [selects, setSelects] = useState({
    banks: [],
    dniTypes: [],
    phoneCodes: [],
  });

  const [paymentForm, setPaymentForm] = useState({
    conversion: 0,
    paymentType: Constants.PAYMENT.TYPE.FEE,
    purchaseProductId: '',
    subtotal: 0,
    igtf: 0,
    iva: 0,
    amount: 0,
    amountVED: 0,
    fees_qty: 0,

    paymentMethodId: '',
    bankId: '',
    date: moment().format('YYYY-MM-DD'),
    dniType: '',
    dni: '',
    phoneCode: '',
    phone: '',
    reference: '',
    name: '',
    account: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

	const getSelects = async () => {
		Promise.all([
			GeneralService.getBanks(),
			GeneralService.getDocumentTypes(),
			GeneralService.getPhoneCodes(),
			GeneralService.findConversion(),
		])
			.then(res => {
				const conversion = res[3].find(x => x.currency_id === Constants.CURRENCY.USD)?.amount ?? 0;

				setSelects({
					banks: res[0].map(x => ({ value: x.id, label: x.name })) ?? [],
					dniTypes: res[1] ?? [],
					phoneCodes: res[2] ?? [],
				});
        setPaymentForm(s => ({ ...s, conversion }));
			})
			.catch(console.log);
	};

  const searchUser = async () => {
    if (!searchDni) return;
    setIsSearching(true);
    setSelectedUser(null);
    setPendingPayment();
    try {
      const res = await UsersService.admin.findAll({ search: searchDni, perPage: 10 });
      setUsers(res?.data || []);
      if (!res?.data?.length) toast.error('No se encontraron usuarios con esa cédula');
    } catch (error) {
      toast.error(String(error));
    } finally {
      setIsSearching(false);
    }
  };

  const selectUser = async (user) => {
    setSelectedUser(user);
    setUsers([]);
    setIsLoadingSubs(true);
    try {
      const res = await PaymentsService.admin.findAPSFeesToPay({ user_id: user.id });
      if (!res?.data?.length) return toast.error('El usuario no tiene cuotas pendientes');
      const item = res?.data?.at(0);

      const initialDate = moment(moment(item.created_at).format('YYYY-MM-DD'));
      const currentFee = item.fees - item.pending_fees;
      const fees = [];

      const _getStatus = (fee, date) => {
        if (fee <= currentFee) {
          return {
            label: 'Pagada',
            color: 'text-green',
            isPayable: false,
          };
        }

        return {
          label: getReadableStatus(date),
          color: getReadableStatusColor(date),
          isPayable: true,
        };
      };

      for (let fee = 1; fee <= item.fees; fee++) {
        const date = initialDate.clone().add(fee - 1, 'months');
        const status = _getStatus(fee, date);

        fees.push({
          feeNumber: `${fee}/${item.fees}`,
          date: date.format('DD-MM-YYYY'),
          status: status.label,
          statusColor: status.color,
          amount: item.price,
          isPayable: status.isPayable,
          isSelected: false,
        });
      }

      setFees(fees);
      // this.setState({ selectedProduct: { ...item }, fees });
      setPendingPayment(res?.data?.at(0) || []);
      setPaymentForm(s => ({ ...s, purchaseProductId: res?.data?.at(0)?.id }));

    } catch (error) {
      toast.error(String(error));
    } finally {
      setIsLoadingSubs(false);
    }
  };

  const clearSelectedUser = () => {
    setSelectedUser(null);
    setPendingPayment();
    setPaymentForm(s => ({ ...s, amount: '', reference: '', bank_name: '', payer_name: '', date: moment().format('YYYY-MM-DD') }));
  };

  const onChangePaymentForm = (value, field) => {
    setPaymentForm(s => ({ ...s, [field]: value }));
  };

  const onToggleSelectToPay = (index) => {
    const _fees = clone(fees.map(x => ({ ...x, isSelected: false })));
    const firstToPayIdx = fees.findIndex(x => x.isPayable);
    if (firstToPayIdx === -1) return;

    if (index === firstToPayIdx) {
      if (!fees[index].isSelected || fees.filter(x => x.isSelected).length > 1) {
        _fees[index].isSelected = true;
      }

    } else {
      for (let i = firstToPayIdx; i <= index; i++) {
        _fees[i].isSelected = true;
      }
    }

    setFees(_fees);
  };

  const onSubmit = async () => {
    if (!selectedUser)
      return toast.error('Debe seleccionar un usuario');

    if (!pendingPayment?.id)
      return toast.error('Debe seleccionar un producto');

    if (!paymentForm.amount || Number(paymentForm.amount) <= 0)
      return toast.error('El monto es obligatorio');

    if (!paymentForm.paymentMethodId)
      return toast.error('Debe seleccionar un método de pago');

    const fields = PAYMENT_EXTRA_FIELDS[Number(paymentForm.paymentMethodId)] || [];
    const errorField = fields.find(field => !paymentForm[field]);
    if (errorField)
      return toast.error(`El campo ${String(FIELD_NAMES[errorField]).toLowerCase()} es obligatorio`);

    setIsSubmitting(true);
    try {
      const payload = {
        ...paymentForm,
        paymentMethodId: Number(paymentForm.paymentMethodId),
      }
      await PaymentsService.admin.createFee(payload);
      toast.success('Pago registrado con éxito');
      onSuccess();
    } catch (error) {
      toast.error(String(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const payingFees = fees.filter(x => x.isPayable && x.isSelected).length;
    const subtotal = round(pendingPayment.price * payingFees);
    const iva = 0;
    const igtf = FOREIGN_METHODS.includes(Number(paymentForm.paymentMethodId))
      ? round((subtotal * Constants.TAXES.IGTF) / 100)
      : 0;
    const amount = round(subtotal + igtf);

    setPaymentForm(s => ({
      ...s,
      subtotal,
      iva,
      igtf,
      amount,
      amountVED: round(amount * s.conversion),
      fees_qty: payingFees,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fees, paymentForm.paymentMethodId]);

  useEffect(() => {
    getSelects();
  }, []);

  const extraFields = PAYMENT_EXTRA_FIELDS[Number(paymentForm.paymentMethodId)] || [];

  return (
    <>
      <ModalHeader className="flex flex-col gap-1">Crear Pago</ModalHeader>
      <ModalBody className="gap-0">
        <div className="space-y-6">
          {!selectedUser ? (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-primary">Buscar Usuario por Cédula</p>
              <div className="flex gap-2">
                <Input
                  classNames={{ inputWrapper: 'border-1 h-10' }}
                  variant="bordered"
                  placeholder="Ingrese número de cédula"
                  value={searchDni}
                  onValueChange={v => setSearchDni(sanitizeToNumbers(v))}
                  startContent={<IconSearch size={18} />}
                  onKeyDown={e => e.key === 'Enter' && searchUser()}
                />
                <Button color="primary" variant="flat" isLoading={isSearching} onPress={searchUser}>
                  Buscar
                </Button>
              </div>

              {!!users.length && (
                <div className="border-1 rounded-lg divide-y">
                  {users.map(user => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-3 cursor-pointer hover:bg-primary-50"
                      onClick={() => selectUser(user)}
                    >
                      <div className="flex items-center gap-3">
                        <IconUser size={20} className="text-primary shrink-0" />
                        <div>
                          <p className="text-sm font-medium">{user.fullName}</p>
                          <p className="text-xs text-gray-500">{user.dni} | {user.email || 'Sin correo'}</p>
                        </div>
                      </div>
                      <Chip size="sm" color="primary" variant="flat">Seleccionar</Chip>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between p-3 bg-primary-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <IconUser size={20} className="text-primary shrink-0" />
                  <div>
                    <p className="text-sm font-medium">{selectedUser.fullName}</p>
                    <p className="text-xs text-gray-500">{selectedUser.dni} | {selectedUser.email || 'Sin correo'}</p>
                  </div>
                </div>
                <Button isIconOnly variant="light" size="sm" onPress={clearSelectedUser}>
                  <IconX size={18} />
                </Button>
              </div>

              <Divider className="my-2" />

              <div>
                <p className="text-sm font-semibold text-primary mb-2">Cuotas</p>
                {isLoadingSubs ? (
                  <div className="flex justify-center py-4"><Spinner /></div>
                ) : !pendingPayment ? (
                  <p className="text-sm text-gray-500">No hay pagos pendiente para este usuario</p>
                ) : (
                  <div className="max-h-64 overflow-y-auto">
                    <div className="w-full my-4">
                      <div className="w-full grid grid-cols-4 gap-x-2 border-b border-b-gray6 font-semibold">
                        <p className="text-left">Fecha</p>
                        <p className="px-2 sm:p-0">Cuota</p>
                        <p>Estatus</p>
                        <p className="text-right">Monto</p>
                      </div>
                      {fees.map((item, idx) => (
                        <div key={idx} className="w-full grid grid-cols-4 gap-x-2 items-center py-1 border-b border-b-gray5 text-sm">
                          <div className="flex items-center gap-x-2">
                            {item.isPayable ? (
                              <button
                                className={classNames('w-[18px] aspect-square rounded-md border-2', item.isSelected ? 'bg-green border-green' : 'bg-transparent border-gray6')}
                                onClick={() => onToggleSelectToPay(idx)}
                              />
                            ) : (
                              <div className="w-4" />
                            )}
                            <p>{ item?.date }</p>
                          </div>
                          <p className="px-2 sm:p-0">{ item?.feeNumber }</p>
                          <p className={classNames('font-semibold', item?.statusColor)}>
                            { item?.status }
                          </p>
                          <p className="text-right">{ formatAmount(item?.amount) }</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <p className="text-sm font-semibold">Total ($): { formatAmount(paymentForm.amount) }</p>
                <p className="text-sm font-semibold">Total (Bs.): { formatAmount(paymentForm.amountVED) }</p>
              </div>

              {pendingPayment && (
                <>
                  <Divider className="my-1" />
                  <p className="text-sm font-semibold text-primary mb-0">Datos del Pago</p>

                  <div className="grid grid-cols-2 gap-4">
                    <Select
                      label="Método de pago"
                      labelPlacement="outside"
                      placeholder="Seleccionar"
                      variant="bordered"
                      classNames={{ trigger: 'border-1 h-10' }}
                      disallowEmptySelection={true}
                      selectedKeys={paymentForm.paymentMethodId ? [paymentForm.paymentMethodId] : []}
                      onSelectionChange={v => onChangePaymentForm(v.currentKey, 'paymentMethodId')}
                    >
                      {PAYMENT_METHODS.map(item => (
                        <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                      ))}
                    </Select>

                    <Input
                      type="date"
                      classNames={{ inputWrapper: 'border-1 h-10' }}
                      variant="bordered"
                      label="Fecha de pago"
                      labelPlacement="outside"
                      placeholder=" "
                      value={paymentForm.date}
                      onValueChange={v => onChangePaymentForm(v, 'date')}
                    />
                    {/* <Input
                      type="number"
                      step="0.01"
                      label="Monto pagado ($)"
                      labelPlacement="outside"
                      placeholder=" "
                      classNames={{ inputWrapper: 'border-1 h-10' }}
                      variant="bordered"
                      value={paymentForm.amount}
                      onValueChange={v => onChangePaymentForm(v, 'amount')}
                    /> */}

                    {extraFields.includes('bankId') && (
                      <Select
                        label="Banco"
                        labelPlacement="outside"
                        placeholder="Seleccionar"
                        variant="bordered"
                        classNames={{ trigger: 'border-1 h-10' }}
                        disallowEmptySelection={true}
                        selectedKeys={paymentForm.bankId ? [paymentForm.bankId] : []}
                        onSelectionChange={v => onChangePaymentForm(v.currentKey, 'bankId')}
                      >
                        {selects.banks.map(item => (
                          <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                        ))}
                      </Select>
                    )}
                    {extraFields.includes('name') && (
                      <Input
                        classNames={{ inputWrapper: 'border-1 h-10' }}
                        variant="bordered"
                        label="Nombre"
                        labelPlacement="outside"
                        placeholder=" "
                        value={paymentForm.name}
                        onValueChange={v => onChangePaymentForm(v, 'name')}
                      />
                    )}
                    {extraFields.includes('dni') && (
                      <div className="flex">
                        <Select
                          label="Cédula"
                          labelPlacement="outside"
                          variant="bordered"
                          placeholder=" "
                          classNames={{
                            base: 'w-1/3',
                            trigger: 'border-1 border-r-0 rounded-tr-none rounded-br-none',
                            label: 'text-nowrap overflow-visible',
                          }}
                          disallowEmptySelection={true}
                          selectedKeys={!!selects.dniTypes.length && !!paymentForm.dniType ? [paymentForm.dniType] : []}
                          onSelectionChange={v => onChangePaymentForm(v.currentKey, 'dniType')}
                        >
                          {selects.dniTypes.map((item) => (
                            <SelectItem key={item.value} value={item.value}>{ item.label }</SelectItem>
                          ))}
                        </Select>
                        <Input
                          label=" "
                          labelPlacement="outside"
                          variant="bordered"
                          classNames={{ base: 'w-2/3', inputWrapper: 'border-1 border-l-0 rounded-tl-none rounded-bl-none' }}
                          value={paymentForm.dni}
                          onValueChange={v => onChangePaymentForm(sanitizeToNumbers(v), 'dni')}
                        />
                      </div>
                    )}
                    {extraFields.includes('phone') && (
                      <div className="flex">
                        <Select
                          label="Teléfono"
                          labelPlacement="outside"
                          variant="bordered"
                          placeholder=" "
                          classNames={{
                            base: 'w-1/3',
                            trigger: 'border-1 border-r-0 rounded-tr-none rounded-br-none',
                            label: 'text-nowrap overflow-visible',
                          }}
                          disallowEmptySelection={true}
                          selectedKeys={!!selects.phoneCodes.length && !!paymentForm.phoneCode ? [paymentForm.phoneCode] : []}
                          onSelectionChange={v => onChangePaymentForm(v.currentKey, 'phoneCode')}
                        >
                          {selects.phoneCodes.map((item) => (
                            <SelectItem key={item.value} value={item.value}>{ item.label }</SelectItem>
                          ))}
                        </Select>
                        <Input
                          label=" "
                          labelPlacement="outside"
                          variant="bordered"
                          classNames={{ base: 'w-2/3', inputWrapper: 'border-1 border-l-0 rounded-tl-none rounded-bl-none' }}
                          value={paymentForm.phone}
                          onValueChange={v => onChangePaymentForm(sanitizeToNumbers(v), 'phone')}
                        />
                      </div>
                    )}
                    {extraFields.includes('account') && (
                      <Input
                        classNames={{ inputWrapper: 'border-1 h-10' }}
                        variant="bordered"
                        label="Correo"
                        labelPlacement="outside"
                        placeholder=" "
                        value={paymentForm.account}
                        onValueChange={v => onChangePaymentForm(v, 'account')}
                      />
                    )}
                    {extraFields.includes('reference') && (
                      <Input
                        classNames={{ inputWrapper: 'border-1 h-10' }}
                        variant="bordered"
                        label="Referencia"
                        labelPlacement="outside"
                        placeholder=" "
                        value={paymentForm.reference}
                        onValueChange={v => onChangePaymentForm(v, 'reference')}
                      />
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="light" onPress={onClose}>Cancelar</Button>
        <Button
          color="primary"
          isDisabled={!pendingPayment || isSubmitting}
          isLoading={isSubmitting}
          onPress={onSubmit}
        >
          Guardar
        </Button>
      </ModalFooter>
    </>
  );
};

export default ModalCreatePayment;
